import { Signal } from 'typed-signals';
import type { Container } from 'pixi.js';
import { getLocalizedValue } from '@softgames/localization';
import type { Popup } from '../../../popups/Popup';
import { WinStreakRoadPopupLayout } from '../../../../layouts/winStreak/WinStreakRoadPopupLayout';
import { getLocalizedString } from '../../../app/localization/getLocalizedString';
import { WinStreakRoadView } from './WinStreakRoadView';
import ScrollBehaviour, { Direction } from '../../../utils/ScrollBehaviour';
import MathUtils from '../../../utils/MathUtils';
import type { WinStreakRoadMapData } from '../types';
import { attachIntervalToDisplayObject, CountDownFormat, formatIntoUnitCountdown } from '../../../utils/time-utils';
import type { WinStreakState } from '../WinStreakState';
import { WinStreakChestProgressBarHandler } from './WinStreakChestProgressBarHandler';
import { generateWinStreakRoadData } from '../model/generateWinStreakRoadData';
import { getCurrentChestProgressByLevel, getCurrentChestProgressVisual, getWinStreakEndTime } from '../WinStreakUtils';
import { waitForNextFrame } from '../../../utils/async-utils';
import { WinStreakTutorialPopupLayout } from '../../../../layouts/winStreak/WinStreakTutorialPopupLayout';
import { WinStreakPathNodeCheckType } from './WinStreakPathNodeView';
import { getSafeAreaInset } from '../../../safearea/SafeAreaProvider';

interface WinStreakPopupConfig {
  levelNumber: number;
  winStreakState: WinStreakState;
  chestToOpen: number;
  intro: boolean;
}

export class WinStreakRoadPopup implements Popup {
  public static readonly requiredLayouts = [WinStreakRoadPopupLayout];

  public readonly onCloseClicked = new Signal();

  public readonly onPlayClicked = new Signal();

  public readonly onAnimationFinished = new Signal();

  private readonly onTutorialClose = new Signal();

  private readonly layout = new WinStreakRoadPopupLayout();

  private tutorialView: Container | null = null;

  private readonly progressBarHandler: WinStreakChestProgressBarHandler;

  private readonly roadData: WinStreakRoadMapData;

  public readonly root = this.layout.root;

  public readonly roadView: WinStreakRoadView;

  private readonly scrollBehaviour: ScrollBehaviour;

  private readonly scrollMinMax = { min: 0, max: 0 };

  constructor(public readonly config: WinStreakPopupConfig) {
    const { winStreakState } = config;

    const progressToShowFirst = winStreakState.lastSeenProgress ?? winStreakState.currentProgress;

    const { current, max } = getCurrentChestProgressVisual(winStreakState, progressToShowFirst);
    this.progressBarHandler = new WinStreakChestProgressBarHandler(this.layout, current, max);

    this.roadData = generateWinStreakRoadData(config.winStreakState);

    this.roadView = new WinStreakRoadView({
      roadMapData: this.roadData,
      userProgress: winStreakState.lastSeenProgress ?? winStreakState.currentProgress,
      mask: this.layout.roadFrame,
    }).props({
      parent: this.layout.mapSlot,
    });

    this.scrollMinMax.max = this.roadView.getHeight() - this.layout.mapSlotMask.height;

    this.scrollBehaviour = new ScrollBehaviour({
      verticalScroll: this.scrollMinMax,
      inputLayer: this.layout.scrollViewInput,
      renderer: this.root.pixi.renderer,
      target: this.roadView,
      rubberBanding: true,
      wheelDirection: Direction.Vertical,
    });

    this.layout.scrollViewInput.on('pointerdown', () => {
      this.roadView.hideAllRewardBubbles();
    });

    this.scrollBehaviour.onTargetPositionChanged.connect(() => {
      this.roadView.update();
    });

    void this.init();

    this.root.once('added', () => {
      this.roadView.update();
    });
  }

  private async init(): Promise<void> {
    const {
      mapSlot,
      mapSlotMask,
      roadFrame,
      scrollViewInput,
      helpButton,
      closeButton,
      playButton,
      playButtonLevelText,
      timerText,
      contentWrapper,
    } = this.layout;

    const { levelNumber, winStreakState, intro } = this.config;

    // it blocks scroll input
    roadFrame.eventMode = 'none';
    scrollViewInput.eventMode = 'static';
    this.root.alpha = 0;

    playButtonLevelText.text = getLocalizedString(`!{Level} ${getLocalizedValue(levelNumber)}`);

    mapSlot.mask = mapSlotMask;
    helpButton.on('pointertap', () => {
      void this.openTutorial();
    });

    closeButton.on('pointertap', () => {
      this.onCloseClicked.emit();
    });

    playButton.root.on('pointertap', () => {
      this.onPlayClicked.emit();
    });

    attachIntervalToDisplayObject(
      timerText,
      () => {
        const timeLeft = getWinStreakEndTime(winStreakState) - Date.now();

        if (timeLeft < 0) {
          timerText.text = getLocalizedString('Ended');
          return;
        }

        timerText.text = formatIntoUnitCountdown(timeLeft, 2, CountDownFormat.DHM);
      },
      10000,
    );

    if (intro) await this.openTutorial(true);

    await waitForNextFrame();
    contentWrapper.interactiveChildren = false;

    await this.handleFlow();

    await waitForNextFrame();
    contentWrapper.interactiveChildren = true;
  }

  private async handleFlow(): Promise<void> {
    const {
      winStreakState: { lastSeenProgress },
      winStreakState,
    } = this.config;

    this.centerScrollViewOnPoint(this.roadView.getPlayerView().y);

    if (lastSeenProgress !== null) {
      await this.animateChange();
    }

    if (this.config.chestToOpen !== -1) {
      this.changeRoadToCheckmarks(this.config.chestToOpen);
      await this.root.wait(200);
      await this.animateChestOpening(this.config.chestToOpen);
      void this.animateBackgroundOverlay();
    }

    this.onAnimationFinished.emit();

    if (this.config.chestToOpen !== -1) {
      const { current, max } = getCurrentChestProgressByLevel(winStreakState);
      this.progressBarHandler.setNewProgress(current, max);
    }
  }

  private changeRoadToCheckmarks(chestIndex: number): void {
    const {
      winStreakState: {
        config: { chests },
      },
    } = this.config;
    const chestData = chests[chestIndex];
    if (!chestData) return;

    for (let i = 0; i <= chestData.level; i += 1) {
      this.roadView.setNodeCheckmark(i, WinStreakPathNodeCheckType.Green);
    }
  }

  private async animateBackgroundOverlay(): Promise<void> {
    const { backgroundOverlay } = this.layout;
    await backgroundOverlay.tweenTo({ alpha: 1 }, 100, 'cubicIn', true).asyncOnComplete();
    await backgroundOverlay.tweenTo({ alpha: 0 }, 800, 'sinOut', true).asyncOnComplete();
  }

  private async animateChestOpening(chestIndex: number): Promise<void> {
    const chestView = this.roadView.getChestView(chestIndex);
    if (!chestView) return;
    await chestView.open();
  }

  private async animateChange(): Promise<void> {
    const {
      roadView,
      roadData,
      config: {
        winStreakState: { currentProgress, lastSeenProgress },
      },
    } = this;

    if (lastSeenProgress === null) return;
    if (currentProgress === lastSeenProgress) return;

    const playerView = roadView.getPlayerView();

    await playerView.hideLevelNumber();

    const direction = lastSeenProgress < currentProgress ? 1 : -1;

    const levelsAffected = new Array(Math.abs(lastSeenProgress - currentProgress) + 1)
      .fill(lastSeenProgress)
      .map((v: number, index) => v + index * direction);

    const jumpTargets = levelsAffected
      .slice(1)
      .map((level) => roadData.pathNodes[level])
      .filter(Boolean);

    let currentLevel = lastSeenProgress;

    const keepScrollAtPlayer = (): void => {
      if (playerView.destroyed) return;
      this.centerScrollViewOnPoint(playerView.y);
    };
    this.root.pixi.ticker.add(keepScrollAtPlayer);

    // eslint-disable-next-line no-restricted-syntax
    for (const target of jumpTargets) {
      this.roadView.setNodeCheckmark(
        currentLevel,
        direction === 1 ? WinStreakPathNodeCheckType.Yellow : WinStreakPathNodeCheckType.None,
      );

      currentLevel += direction;
      const { current, max } = getCurrentChestProgressVisual(this.config.winStreakState, currentLevel);
      this.progressBarHandler.animateChange(current === 0 && direction === 1 ? max : current, max);
      await playerView.jump(target);
    }

    this.root.pixi.ticker.remove(keepScrollAtPlayer);

    await playerView.showLevelNumber(currentProgress);
  }

  private centerScrollViewOnPoint(y: number): void {
    const { min, max } = this.scrollMinMax;
    this.roadView.props({
      y: MathUtils.clamp((y + this.layout.mapSlotMask.height * 0.4) * -1, min, max),
    });
  }

  private openTutorial(skipAnimation = false): Promise<void> {
    const onTutorialClosed = new Signal();

    if (!this.tutorialView) {
      const { root, continueButton } = new WinStreakTutorialPopupLayout();
      this.tutorialView = root.props({
        parent: this.layout.tutorialSlot,
        y: -100,
      });

      continueButton.on('pointertap', () => {
        void this.closeTutorial();
        onTutorialClosed.emit();
      });
    }

    const { tutorialSlot, popup } = this.layout;

    this.layout.tutorialSlot.interactiveChildren = true;

    if (skipAnimation) {
      tutorialSlot.alpha = 1;
      popup.alpha = 0;
    } else {
      tutorialSlot.tweenTo({ alpha: 1 }, 300, 'sinIn', true);
      popup.tweenTo({ alpha: 0 }, 300, 'sinIn', true);
    }

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    const { promise, resolve } = Promise.withResolvers<void>();
    onTutorialClosed.connect(resolve);
    return promise;
  }

  private async closeTutorial(): Promise<void> {
    const { tutorialSlot, popup } = this.layout;
    tutorialSlot.interactiveChildren = false;
    tutorialSlot.tweenTo({ alpha: 0 }, 300, 'sinIn', true);
    await popup.tweenTo({ alpha: 1 }, 300, 'sinIn', true).asyncOnComplete();
    this.onTutorialClose.emit();
  }

  public resize(): void {
    const { gradient, popup, root, tutorialSlot, header, flag } = this.layout;
    const safeArea = getSafeAreaInset();

    const {
      pixi: {
        renderer: { width, height },
      },
    } = gradient;

    root.y = Math.ceil(height * -0.5);

    gradient.props({
      width: width * 1.1,
      height: height * 1.1,
    });

    // Apply safe area to header container and flag
    header.y = safeArea.top;
    flag.y = safeArea.top;

    popup.y = Math.floor(height * 0.57);
    tutorialSlot.y = popup.y;
  }
}
